import { CoreModalDialogFooter, MMButton } from '@components'
import TransactionDetails from '@views/transactions/TransactionDetails'
import { paths } from 'mmfintech-backend-api'
import type { ActiveDocumentRequest } from 'mmfintech-backend-api/types'
import { formatDateTime, formatFloat, GlobalContext, tr } from 'mmfintech-commons'
import { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { DocumentRequestUploadModal } from '../DocumentRequestUploadModal'
import styles from './DocumentRequestPopup.module.scss'

interface DocumentRequestPopupProps {
  requests: ActiveDocumentRequest[]
}

export const DocumentRequestPopup = ({ requests }: DocumentRequestPopupProps) => {
  const { modalHide, modalShow } = useContext(GlobalContext)
  const history = useHistory()

  const handleUpload = (docRequestId: number) => {
    modalShow({
      header: tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.UPLOAD.TITLE', 'Upload Document'),
      content: <DocumentRequestUploadModal docRequestId={docRequestId} />
    })
  }

  const handleView = () => {
    if (requests.length === 1) {
      modalShow({
        header: tr('FRONTEND.TRANSACTIONS.DETAILS.TITLE', 'Transaction details'),
        content: <TransactionDetails isModal={true} transactionId={requests[0].transactionId} />,
        options: { size: 'auto' }
      })
    } else {
      modalHide()
      history.push(paths.banking.transactions.list())
    }
  }

  return (
    <div className={styles['document-request-popup-wrapper']} data-test='document-request-popup'>
      <div className={styles['request-list']}>
        {requests.map(request => (
          <div className={styles['request-row']} key={request.documentRequestId} data-test='document-request-row'>
            <div className={styles['request-details']}>
              <span className={styles['request-id']}>
                {tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.POPUP.TRANSACTION_ID', 'Transaction ID')}:{' '}
                {request.transactionId}
              </span>
              <span className={styles['request-amount']}>
                {formatFloat(request.amount)} {request.currency}
              </span>
              <span className={styles['request-date']}>{formatDateTime(request.date)}</span>
              {request.adminComment && (
                <span className={styles['request-comment']}>
                  {tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.POPUP.BACK_OFFICE_COMMENT', 'Back office comment')}:{' '}
                  {request.adminComment}
                </span>
              )}
            </div>
            <MMButton
              type='button'
              variant='secondary'
              caption={tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.POPUP.UPLOAD_BUTTON', 'Upload Document')}
              onClick={() => handleUpload(request.documentRequestId)}
              data-test='button-upload'
            />
          </div>
        ))}
      </div>

      <CoreModalDialogFooter>
        <div className={styles.buttons}>
          <MMButton
            type='button'
            size='large'
            variant='primary'
            fullWidth
            caption={
              requests.length === 1
                ? tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.POPUP.VIEW_TRANSACTION', 'View Transaction')
                : tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.POPUP.VIEW_TRANSACTIONS', 'View Transactions')
            }
            onClick={handleView}
            data-test='button-view'
          />
          <MMButton
            type='button'
            size='large'
            variant='tertiary'
            className={styles['dismiss-button']}
            caption={tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.POPUP.DISMISS', 'Dismiss')}
            onClick={modalHide}
            data-test='button-dismiss'
          />
        </div>
      </CoreModalDialogFooter>
    </div>
  )
}
